import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { NotificationProvider } from "@/context/NotificationContext";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Feed from "@/pages/Feed";
import Upload from "@/pages/Upload";
import Profile from "@/pages/Profile";
import Inbox from "@/pages/Inbox";
import AdminDashboard from "@/pages/AdminDashboard";
import OwnerDashboard from "@/pages/OwnerDashboard";
import AuthCallback from "@/pages/AuthCallback";
import PublicProfile from "@/pages/PublicProfile";
import Shortlist from "@/pages/Shortlist";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#68706B]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/feed" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#68706B]">Loading…</div>;
  if (user) return <Navigate to="/feed" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Detect Emergent Google session_id synchronously during render to prevent races
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
        <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
        <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
        <Route path="/feed" element={<Protected><Feed /></Protected>} />
        <Route path="/upload" element={<Protected roles={["candidate"]}><Upload /></Protected>} />
        <Route path="/profile" element={<Protected><Profile /></Protected>} />
        <Route path="/inbox" element={<Protected><Inbox /></Protected>} />
        <Route path="/shortlist" element={<Protected roles={["hr", "owner"]}><Shortlist /></Protected>} />
        <Route path="/p/:userId" element={<PublicProfile />} />
        <Route path="/admin" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
        <Route path="/owner" element={<Protected roles={["owner"]}><OwnerDashboard /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <NotificationProvider>
          <AppRouter />
          <Toaster position="top-center" />
        </NotificationProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}
